/**
 * EditMessageButtons — the action group for the edit-a-sent-message box:
 * Attach (optional), Send, Cancel. Shared by ChatPanel, HeroChat and
 * MessageBubble's fallback so the three edit surfaces can't drift apart.
 *
 * Icon-only, Send before Cancel, and the three move together. `ml-auto`
 * right-aligns the group, including when the caller's row wraps it onto a
 * line of its own — which it does only when the group and the model/context
 * chips genuinely can't share one.
 */
import { Paperclip, Send, X } from 'lucide-react'

interface Props {
  onSend: () => void
  onCancel: () => void
  sendDisabled: boolean
  /** Opens the file picker; omit to hide the paperclip. */
  onAttach?: () => void
}

export function EditMessageButtons({ onSend, onCancel, sendDisabled, onAttach }: Props) {
  return (
    <div className="flex items-center gap-2 shrink-0 ml-auto">
      {onAttach && (
        <button
          type="button"
          onClick={onAttach}
          title="Attach file"
          aria-label="Attach file"
          className="themed-text-muted hover:themed-text transition-colors p-0.5"
        >
          <Paperclip size={14} />
        </button>
      )}
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onSend}
          disabled={sendDisabled}
          title="Send (Enter)"
          aria-label="Send"
          className="p-1 rounded text-brand-300 hover:themed-bg-secondary transition-colors disabled:opacity-30"
        >
          <Send size={14} />
        </button>
        <button
          type="button"
          onClick={onCancel}
          title="Cancel (Esc)"
          aria-label="Cancel"
          className="p-1 rounded themed-text-muted hover:themed-text hover:themed-bg-secondary transition-colors"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  )
}
